import { useState, useEffect, useMemo } from 'react'
import {
  Box,
  Typography,
  Paper,
  Grid,
  Stack,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Button,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Alert,
  LinearProgress,
  Tooltip,
  IconButton,
  Pagination,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Avatar,
} from '@mui/material'
import WarningIcon from '@mui/icons-material/Warning'
import SecurityIcon from '@mui/icons-material/Security'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import AddIcon from '@mui/icons-material/Add'
import EditIcon from '@mui/icons-material/Edit'
import DeleteIcon from '@mui/icons-material/Delete'
import SendIcon from '@mui/icons-material/Send'
import EmailIcon from '@mui/icons-material/Email'
import Tabs from '@mui/material/Tabs'
import Tab from '@mui/material/Tab'
import Dialog from '@mui/material/Dialog'
import DialogTitle from '@mui/material/DialogTitle'
import DialogContent from '@mui/material/DialogContent'
import DialogActions from '@mui/material/DialogActions'
import Switch from '@mui/material/Switch'
import FormControlLabel from '@mui/material/FormControlLabel'
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, Legend, PieChart, Pie, Cell, BarChart, Bar, AreaChart, Area } from 'recharts'
import TrendingUpIcon from '@mui/icons-material/TrendingUp'
import TrendingDownIcon from '@mui/icons-material/TrendingDown'
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive'
import TimelineIcon from '@mui/icons-material/Timeline'
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet'
import PhoneAndroidIcon from '@mui/icons-material/PhoneAndroid'
import DateRangeIcon from '@mui/icons-material/DateRange'
import RefreshIcon from '@mui/icons-material/Refresh'
import { transactionService } from '@/services/transactionService'
import { DepositTransaction, WithdrawalTransaction } from '@/types/transactions'
import TransactionDetailModal from '@/components/TransactionDetailModal'
import { Deposit, Withdrawal } from '@/types/transactions'
import { fraudService, FraudRule, FraudNotificationEmail } from '@/services/fraudService'

interface FraudRisk {
  id: string
  transactionId?: string
  type: 'deposit' | 'withdrawal'
  msisdn: string
  amount: number
  riskScore: number
  riskLevel: 'low' | 'medium' | 'high' | 'critical'
  flags: string[]
  reason: string
  timestamp: string
  detectedAt?: string
  transaction: DepositTransaction | WithdrawalTransaction
}

const FraudDetection = () => {
  const [tabValue, setTabValue] = useState(0)
  const [risks, setRisks] = useState<FraudRisk[]>([])
  const [loading, setLoading] = useState(false)
  const [filter, setFilter] = useState<'all' | 'high' | 'critical'>('all')
  const [selectedTransaction, setSelectedTransaction] = useState<Deposit | Withdrawal | null>(null)
  const [modalOpen, setModalOpen] = useState(false)
  const [modalType, setModalType] = useState<'deposit' | 'withdrawal'>('deposit')
  
  // Pagination state
  const [page, setPage] = useState(1)
  const [rowsPerPage] = useState(10)
  
  // Time range filter
  const [timeRange, setTimeRange] = useState<'1h' | '24h' | '7d' | '30d'>('24h')
  
  // Rule management state
  const [rules, setRules] = useState<FraudRule[]>([])
  const [loadingRules, setLoadingRules] = useState(false)
  const [ruleDialogOpen, setRuleDialogOpen] = useState(false)
  const [editingRule, setEditingRule] = useState<FraudRule | null>(null)
  const [ruleForm, setRuleForm] = useState<Partial<FraudRule>>({
    name: '',
    description: '',
    enabled: true,
    ruleType: 'amount',
    conditionParams: {},
    riskScore: 0,
    action: 'flag',
    priority: 0,
    scheduleInterval: 0,
  })

  // Notification email management state
  const [notificationEmails, setNotificationEmails] = useState<FraudNotificationEmail[]>([])
  const [loadingEmails, setLoadingEmails] = useState(false)
  const [emailDialogOpen, setEmailDialogOpen] = useState(false)
  const [editingEmail, setEditingEmail] = useState<FraudNotificationEmail | null>(null)
  const [emailForm, setEmailForm] = useState<Partial<FraudNotificationEmail>>({
    email: '',
    enabled: true,
    notifyOnCritical: true,
    notifyOnHigh: true,
    notifyOnMedium: false,
    notifyOnLow: false,
  })

  // Test rule state
  const [testDialogOpen, setTestDialogOpen] = useState(false)
  const [testingRule, setTestingRule] = useState<FraudRule | null>(null)
  const [testResult, setTestResult] = useState<any>(null)
  const [testing, setTesting] = useState(false)

  useEffect(() => {
    loadFraudRisks()
    loadFraudRules()
    loadNotificationEmails()
    const interval = setInterval(loadFraudRisks, 60000) // Refresh every minute
    return () => clearInterval(interval)
  }, [])

  const loadFraudRules = async () => {
    setLoadingRules(true)
    try {
      const fetchedRules = await fraudService.getFraudRules()
      setRules(fetchedRules)
    } catch (error) {
      console.error('Failed to load fraud rules', error)
    } finally {
      setLoadingRules(false)
    }
  }

  const loadNotificationEmails = async () => {
    setLoadingEmails(true)
    try {
      const fetchedEmails = await fraudService.getNotificationEmails()
      setNotificationEmails(fetchedEmails)
    } catch (error) {
      console.error('Failed to load notification emails', error)
    } finally {
      setLoadingEmails(false)
    }
  }

  const loadFraudRisks = async () => {
    setLoading(true)
    try {
      // Use backend fraud detection service
      const risks = await fraudService.getFraudRisks({ level: filter === 'all' ? undefined : filter })
      
      // Convert backend format to frontend format and fetch transaction details
      const risksWithTransactions: FraudRisk[] = []
      
      for (const risk of risks) {
        try {
          // Fetch transaction details
          let transaction: DepositTransaction | WithdrawalTransaction | null = null
          const txType = risk.type || 'deposit' // Default to deposit if type not provided
          
          if (risk.transactionId) {
            if (txType === 'deposit') {
              const deposits = await transactionService.fetchDeposits({ page: 1, page_size: 100 })
              transaction = deposits.items.find((d: any) => d.id === risk.transactionId) as DepositTransaction | undefined || null
            } else {
              const withdrawals = await transactionService.fetchWithdrawals({ page: 1, page_size: 100 })
              transaction = withdrawals.items.find((w: any) => w.id === risk.transactionId) as WithdrawalTransaction | undefined || null
            }
          }
          
          // Create risk entry even if transaction not found (for display purposes)
          risksWithTransactions.push({
            id: risk.id || risk.transactionId,
            transactionId: risk.transactionId,
            type: txType as 'deposit' | 'withdrawal',
            msisdn: risk.msisdn,
            amount: risk.amount,
            riskScore: risk.riskScore,
            riskLevel: risk.riskLevel,
            flags: risk.flags || [],
            reason: risk.reason,
            detectedAt: risk.detectedAt,
            timestamp: risk.detectedAt || risk.timestamp || new Date().toISOString(),
            transaction: transaction || {
              id: risk.transactionId,
              msisdn: risk.msisdn,
              amount: risk.amount,
              type: txType,
            } as any,
          })
        } catch (err) {
          console.error('Failed to fetch transaction details for risk', risk.id, err)
          // Still add the risk even if transaction fetch fails
          risksWithTransactions.push({
            id: risk.id || risk.transactionId,
            transactionId: risk.transactionId,
            type: (risk.type || 'deposit') as 'deposit' | 'withdrawal',
            msisdn: risk.msisdn,
            amount: risk.amount,
            riskScore: risk.riskScore,
            riskLevel: risk.riskLevel,
            flags: risk.flags || [],
            reason: risk.reason,
            detectedAt: risk.detectedAt,
            timestamp: risk.detectedAt || risk.timestamp || new Date().toISOString(),
            transaction: {
              id: risk.transactionId,
              msisdn: risk.msisdn,
              amount: risk.amount,
            } as any,
          })
        }
      }
      
      setRisks(risksWithTransactions)
    } catch (error) {
      console.error('Failed to load fraud risks', error)
      setRisks([])
    } finally {
      setLoading(false)
    }
  }

  const filteredRisks = useMemo(() => {
    let filtered = risks
    if (filter === 'high') filtered = risks.filter((r) => r.riskLevel === 'high' || r.riskLevel === 'critical')
    else if (filter === 'critical') filtered = risks.filter((r) => r.riskLevel === 'critical')
    
    // Sort by risk score descending, then by timestamp
    return filtered.sort((a, b) => {
      if (b.riskScore !== a.riskScore) return b.riskScore - a.riskScore
      return new Date(b.timestamp || b.detectedAt || '').getTime() - new Date(a.timestamp || a.detectedAt || '').getTime()
    })
  }, [risks, filter])
  
  // Paginated risks
  const paginatedRisks = useMemo(() => {
    const startIndex = (page - 1) * rowsPerPage
    return filteredRisks.slice(startIndex, startIndex + rowsPerPage)
  }, [filteredRisks, page, rowsPerPage])
  
  const totalPages = Math.ceil(filteredRisks.length / rowsPerPage)

  const stats = useMemo(() => {
    const total = risks.length
    const critical = risks.filter((r) => r.riskLevel === 'critical').length
    const high = risks.filter((r) => r.riskLevel === 'high').length
    const medium = risks.filter((r) => r.riskLevel === 'medium').length
    const totalAmount = risks.reduce((sum, r) => sum + r.amount, 0)
    const avgRiskScore = total > 0 ? risks.reduce((sum, r) => sum + r.riskScore, 0) / total : 0
    
    return {
      total,
      critical,
      high,
      medium,
      totalAmount,
      avgRiskScore: Math.round(avgRiskScore),
    }
  }, [risks])
  
  // Chart data
  const chartData = useMemo(() => {
    // Risk level distribution
    const riskDistribution = [
      { name: 'Critical', value: stats.critical, color: '#d32f2f' },
      { name: 'High', value: stats.high, color: '#ed6c02' },
      { name: 'Medium', value: stats.medium, color: '#0288d1' },
    ]
    
    // Risk trends (group by hour/day)
    const riskTrends = risks.reduce((acc, risk) => {
      const date = new Date(risk.timestamp || risk.detectedAt || '')
      const key = timeRange === '1h' 
        ? `${date.getHours()}:00`
        : timeRange === '24h'
        ? `${date.getDate()}/${date.getMonth() + 1} ${date.getHours()}:00`
        : `${date.getDate()}/${date.getMonth() + 1}`
      
      if (!acc[key]) {
        acc[key] = { time: key, critical: 0, high: 0, medium: 0, total: 0 }
      }
      acc[key].total++
      if (risk.riskLevel === 'critical') acc[key].critical++
      else if (risk.riskLevel === 'high') acc[key].high++
      else acc[key].medium++
      return acc
    }, {} as Record<string, any>)
    
    const trendData = Object.values(riskTrends).sort((a: any, b: any) => {
      return a.time.localeCompare(b.time)
    })
    
    // Top risky MSISDNs
    const msisdnCounts = risks.reduce((acc, risk) => {
      acc[risk.msisdn] = (acc[risk.msisdn] || 0) + 1
      return acc
    }, {} as Record<string, number>)
    
    const topMsisdns = Object.entries(msisdnCounts)
      .map(([msisdn, count]) => ({ msisdn, count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 10)
    
    // Risk by transaction type
    const typeDistribution = risks.reduce((acc, risk) => {
      acc[risk.type] = (acc[risk.type] || 0) + 1
      return acc
    }, {} as Record<string, number>)
    
    const typeData = [
      { name: 'Deposits', value: typeDistribution.deposit || 0 },
      { name: 'Withdrawals', value: typeDistribution.withdrawal || 0 },
    ]
    
    // Risk score distribution
    const scoreRanges = [
      { range: '50-59', min: 50, max: 59 },
      { range: '60-69', min: 60, max: 69 },
      { range: '70-79', min: 70, max: 79 },
      { range: '80-89', min: 80, max: 89 },
      { range: '90+', min: 90, max: 999 },
    ]
    
    const scoreDistribution = scoreRanges.map(range => ({
      range: range.range,
      count: risks.filter(r => r.riskScore >= range.min && r.riskScore <= range.max).length,
    }))
    
    return {
      riskDistribution,
      trendData,
      topMsisdns,
      typeData,
      scoreDistribution,
    }
  }, [risks, timeRange])

  const getRiskColor = (level: string): 'error' | 'warning' | 'info' | 'default' => {
    switch (level) {
      case 'critical':
        return 'error'
      case 'high':
        return 'warning'
      case 'medium':
        return 'info'
      default:
        return 'default'
    }
  }

  const handleRowClick = (risk: FraudRisk) => {
    if (risk.type === 'deposit') {
      const deposit: Deposit = {
        id: risk.transaction.id,
        msisdn: risk.transaction.msisdn,
        amount: risk.transaction.amount,
        amount_sent: (risk.transaction as DepositTransaction).amountSent || risk.transaction.amount,
        applied_tax: (risk.transaction as DepositTransaction).appliedTax || 0,
        paybill: risk.transaction.paybill || '',
        mpesa_receipt_number: (risk.transaction as DepositTransaction).mpesaReceiptNumber || null,
        merchant_request_id: (risk.transaction as DepositTransaction).merchantRequestId,
        checkout_request_id: (risk.transaction as DepositTransaction).checkoutRequestId,
        channel_id: 0,
        status_id: 0,
        external_transaction_id: (risk.transaction as DepositTransaction).externalTransactionId,
        external_user_id: (risk.transaction as DepositTransaction).externalUserId || null,
        comment: null,
        btobet_response: (risk.transaction as DepositTransaction).btobetResponse || null,
        mpesa_response: (risk.transaction as DepositTransaction).mpesaResponse || null,
        channel: { id: 0, name: risk.transaction.channel || 'N/A', description: null },
        status: { id: 0, name: risk.transaction.status || 'PENDING', description: null },
        created_at: risk.transaction.createdAt,
        updated_at: risk.transaction.createdAt,
      }
      setSelectedTransaction(deposit)
      setModalType('deposit')
    } else {
      const withdrawal: Withdrawal = {
        id: risk.transaction.id,
        msisdn: risk.transaction.msisdn,
        amount: risk.transaction.amount,
        amount_sent: (risk.transaction as WithdrawalTransaction).amountSent || risk.transaction.amount,
        applied_tax: (risk.transaction as WithdrawalTransaction).appliedTax || 0,
        paybill: risk.transaction.paybill || '',
        mpesa_receipt_number: (risk.transaction as WithdrawalTransaction).mpesaReceiptNumber || null,
        conversation_id: (risk.transaction as WithdrawalTransaction).conversationId || null,
        originator_conversation_id: (risk.transaction as WithdrawalTransaction).originatorConversationId || null,
        channel_id: 0,
        status_id: 0,
        external_transaction_id: (risk.transaction as WithdrawalTransaction).externalTransactionId,
        external_user_id: (risk.transaction as WithdrawalTransaction).externalUserId || null,
        comment: null,
        btobet_response: (risk.transaction as WithdrawalTransaction).btobetResponse || null,
        mpesa_response: (risk.transaction as WithdrawalTransaction).mpesaResponse || null,
        sms_withdrawal_request_id: null,
        channel: { id: 0, name: risk.transaction.channel || 'N/A', description: null },
        status: { id: 0, name: risk.transaction.status || 'PENDING', description: null },
        created_at: risk.transaction.createdAt,
        updated_at: risk.transaction.createdAt,
      }
      setSelectedTransaction(withdrawal)
      setModalType('withdrawal')
    }
    setModalOpen(true)
  }

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-KE', {
      style: 'currency',
      currency: 'KES',
      maximumFractionDigits: 0,
    }).format(value)
  }

  // Format condition parameters for display
  const formatThreshold = (rule: FraudRule): string => {
    const params = rule.conditionParams || {}
    
    switch (rule.ruleType) {
      case 'amount':
        const amountOp = params.operator || '>'
        const amountVal = params.value || 0
        return `${amountOp} ${formatCurrency(amountVal)}`
      
      case 'frequency':
        const freqOp = params.operator || '>'
        const freqVal = params.value || 0
        const freqWindow = params.time_window || '1h'
        return `${freqOp} ${freqVal} transactions in ${freqWindow}`
      
      case 'volume':
        const volOp = params.operator || '>'
        const volVal = params.value || 0
        const volWindow = params.time_window || '24h'
        return `${volOp} ${formatCurrency(volVal)} in ${volWindow}`
      
      case 'time':
        const startHour = params.start_hour ?? 0
        const endHour = params.end_hour ?? 23
        return `${String(startHour).padStart(2, '0')}:00 - ${String(endHour).padStart(2, '0')}:00`
      
      case 'status':
        return `Status = "${params.status || 'FAILED'}"`
      
      default:
        return JSON.stringify(params)
    }
  }

  // Rule management handlers
  const handleCreateRule = () => {
    setEditingRule(null)
    setRuleForm({
      name: '',
      description: '',
      enabled: true,
      ruleType: 'amount',
      conditionParams: {},
      riskScore: 0,
      action: 'flag',
      priority: 0,
      scheduleInterval: 0,
    })
    setRuleDialogOpen(true)
  }

  const handleEditRule = (rule: FraudRule) => {
    setEditingRule(rule)
    // Ensure conditionParams is an object (handle case where it might be a string)
    let conditionParams = rule.conditionParams
    if (typeof conditionParams === 'string') {
      try {
        conditionParams = JSON.parse(conditionParams)
      } catch {
        conditionParams = {}
      }
    }
    if (!conditionParams || typeof conditionParams !== 'object') {
      conditionParams = {}
    }
    
    setRuleForm({
      name: rule.name || '',
      description: rule.description || '',
      enabled: rule.enabled ?? true,
      ruleType: rule.ruleType || 'amount',
      conditionParams: conditionParams,
      riskScore: rule.riskScore || 0,
      action: rule.action || 'flag',
      priority: rule.priority || 0,
      scheduleInterval: rule.scheduleInterval || 0,
    })
    setRuleDialogOpen(true)
  }

  const handleSaveRule = async () => {
    try {
      // Validate required fields
      if (!ruleForm.name || !ruleForm.name.trim()) {
        alert('Rule name is required')
        return
      }
      
      if (ruleForm.riskScore === undefined || ruleForm.riskScore === null) {
        alert('Risk score is required')
        return
      }
      
      // Ensure conditionParams is properly formatted
      const conditionParams = ruleForm.conditionParams || {}
      
      // Prepare the rule data
      const ruleData = {
        name: ruleForm.name.trim(),
        description: ruleForm.description || '',
        enabled: ruleForm.enabled ?? true,
        ruleType: ruleForm.ruleType || 'amount',
        conditionParams: conditionParams,
        riskScore: ruleForm.riskScore || 0,
        action: ruleForm.action || 'flag',
        priority: ruleForm.priority || 0,
        scheduleInterval: ruleForm.scheduleInterval || 0,
      }
      
      if (editingRule) {
        await fraudService.updateFraudRule(editingRule.id, ruleData)
      } else {
        await fraudService.createFraudRule(ruleData as Omit<FraudRule, 'id' | 'createdAt' | 'updatedAt'>)
      }
      await loadFraudRules()
      setRuleDialogOpen(false)
    } catch (error: any) {
      console.error('Failed to save rule', error)
      alert(`Failed to save rule: ${error?.response?.data?.message || error?.message || 'Unknown error'}`)
    }
  }

  const handleDeleteRule = async (ruleId: string) => {
    if (window.confirm('Are you sure you want to delete this rule?')) {
      try {
        await fraudService.deleteFraudRule(ruleId)
        await loadFraudRules()
      } catch (error) {
        console.error('Failed to delete rule', error)
      }
    }
  }

  const handleToggleRule = async (rule: FraudRule) => {
    try {
      await fraudService.updateFraudRule(rule.id, { enabled: !rule.enabled })
      await loadFraudRules()
    } catch (error) {
      console.error('Failed to toggle rule', error)
    }
  }

  const handleTestRule = async (rule: FraudRule) => {
    setTestingRule(rule)
    setTestResult(null)
    setTestDialogOpen(true)
    setTesting(true)
    
    try {
      const result = await fraudService.testFraudRule(rule.id)
      setTestResult(result)
    } catch (error: any) {
      setTestResult({
        success: false,
        message: error?.response?.data?.message || error?.message || 'Failed to test rule',
        ruleMatched: false,
      })
    } finally {
      setTesting(false)
    }
  }

  // Notification email handlers
  const handleCreateEmail = () => {
    setEditingEmail(null)
    setEmailForm({
      email: '',
      enabled: true,
      notifyOnCritical: true,
      notifyOnHigh: true,
      notifyOnMedium: false,
      notifyOnLow: false,
    })
    setEmailDialogOpen(true)
  }

  const handleEditEmail = (email: FraudNotificationEmail) => {
    setEditingEmail(email)
    setEmailForm({
      email: email.email,
      enabled: email.enabled,
      notifyOnCritical: email.notifyOnCritical,
      notifyOnHigh: email.notifyOnHigh,
      notifyOnMedium: email.notifyOnMedium,
      notifyOnLow: email.notifyOnLow,
    })
    setEmailDialogOpen(true)
  }

  const handleSaveEmail = async () => {
    try {
      if (editingEmail) {
        await fraudService.updateNotificationEmail(editingEmail.id, emailForm)
      } else {
        await fraudService.createNotificationEmail(emailForm as Omit<FraudNotificationEmail, 'id' | 'createdAt' | 'updatedAt'>)
      }
      await loadNotificationEmails()
      setEmailDialogOpen(false)
    } catch (error) {
      console.error('Failed to save notification email', error)
    }
  }

  const handleDeleteEmail = async (emailId: string) => {
    if (window.confirm('Are you sure you want to delete this notification email?')) {
      try {
        await fraudService.deleteNotificationEmail(emailId)
        await loadNotificationEmails()
      } catch (error) {
        console.error('Failed to delete notification email', error)
      }
    }
  }

  const handleToggleEmail = async (email: FraudNotificationEmail) => {
    try {
      await fraudService.updateNotificationEmail(email.id, { enabled: !email.enabled })
      await loadNotificationEmails()
    } catch (error) {
      console.error('Failed to toggle email', error)
    }
  }

  return (
    <Box>
      <Typography variant="h4" component="h1" gutterBottom>
        Fraud Detection Dashboard
      </Typography>
      <Typography variant="body2" color="text.secondary" gutterBottom>
        Monitor and manage suspicious transaction patterns
      </Typography>

      <Paper sx={{ mb: 3, mt: 2 }}>
        <Tabs value={tabValue} onChange={(_, newValue) => setTabValue(newValue)}>
          <Tab label="Fraud Risks" />
          <Tab label="Detection Rules" />
          <Tab label="Notification Emails" />
        </Tabs>
      </Paper>

      {tabValue === 0 && (
        <>
      {/* Header with Time Range Filter */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
          <Box>
            <Typography variant="h5" fontWeight="bold">
              Fraud Detection Overview
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Real-time monitoring of suspicious transaction patterns
            </Typography>
          </Box>
          <Stack direction="row" spacing={2} alignItems="center">
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Time Range</InputLabel>
              <Select
                value={timeRange}
                label="Time Range"
                onChange={(e) => setTimeRange(e.target.value as any)}
              >
                <MenuItem value="1h">Last Hour</MenuItem>
                <MenuItem value="24h">Last 24 Hours</MenuItem>
                <MenuItem value="7d">Last 7 Days</MenuItem>
                <MenuItem value="30d">Last 30 Days</MenuItem>
              </Select>
            </FormControl>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={loadFraudRisks}
              disabled={loading}
            >
              Refresh
            </Button>
          </Stack>
        </Stack>
      </Paper>

      {/* Enhanced Statistics Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card sx={{ height: '100%', background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white' }}>
            <CardContent>
              <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                <Box>
                  <Typography variant="caption" sx={{ opacity: 0.9 }}>
                  Total Risks
                </Typography>
                  <Typography variant="h4" fontWeight="bold" mt={1}>
                {stats.total}
              </Typography>
                </Box>
                <SecurityIcon sx={{ fontSize: 40, opacity: 0.8 }} />
              </Stack>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card sx={{ height: '100%', background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)', color: 'white' }}>
            <CardContent>
              <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                <Box>
                  <Typography variant="caption" sx={{ opacity: 0.9 }}>
                  Critical
                </Typography>
                  <Typography variant="h4" fontWeight="bold" mt={1}>
                {stats.critical}
              </Typography>
                  <Typography variant="caption" sx={{ opacity: 0.8 }}>
                    {stats.total > 0 ? Math.round((stats.critical / stats.total) * 100) : 0}%
                  </Typography>
                </Box>
                <WarningIcon sx={{ fontSize: 40, opacity: 0.8 }} />
              </Stack>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card sx={{ height: '100%', background: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)', color: 'white' }}>
            <CardContent>
              <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                <Box>
                  <Typography variant="caption" sx={{ opacity: 0.9 }}>
                  High Risk
                </Typography>
                  <Typography variant="h4" fontWeight="bold" mt={1}>
                {stats.high}
              </Typography>
                  <Typography variant="caption" sx={{ opacity: 0.8 }}>
                    {stats.total > 0 ? Math.round((stats.high / stats.total) * 100) : 0}%
                  </Typography>
                </Box>
                <WarningIcon sx={{ fontSize: 40, opacity: 0.8 }} />
              </Stack>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card sx={{ height: '100%', background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)', color: 'white' }}>
            <CardContent>
              <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                <Box>
                  <Typography variant="caption" sx={{ opacity: 0.9 }}>
                  Medium Risk
                </Typography>
                  <Typography variant="h4" fontWeight="bold" mt={1}>
                {stats.medium}
              </Typography>
                  <Typography variant="caption" sx={{ opacity: 0.8 }}>
                    {stats.total > 0 ? Math.round((stats.medium / stats.total) * 100) : 0}%
                  </Typography>
                </Box>
                <WarningIcon sx={{ fontSize: 40, opacity: 0.8 }} />
              </Stack>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card sx={{ height: '100%', background: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)', color: 'white' }}>
            <CardContent>
              <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                <Box>
                  <Typography variant="caption" sx={{ opacity: 0.9 }}>
                    Avg Risk Score
                  </Typography>
                  <Typography variant="h4" fontWeight="bold" mt={1}>
                    {stats.avgRiskScore}
                  </Typography>
                  <Typography variant="caption" sx={{ opacity: 0.8 }}>
                    Total: {formatCurrency(stats.totalAmount)}
                  </Typography>
                </Box>
                <TrendingUpIcon sx={{ fontSize: 40, opacity: 0.8 }} />
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Charts Section */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        {/* Risk Level Distribution */}
        <Grid item xs={12} md={4}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Risk Level Distribution
              </Typography>
              {chartData.riskDistribution.some(r => r.value > 0) ? (
                <Box sx={{ height: 300, mt: 2 }}>
                  <ResponsiveContainer>
                    <PieChart>
                      <Pie
                        data={chartData.riskDistribution}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={({ name, value, percent }) => `${name}: ${value} (${(percent * 100).toFixed(0)}%)`}
                        outerRadius={100}
                        fill="#8884d8"
                        dataKey="value"
                      >
                        {chartData.riskDistribution.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <RechartsTooltip />
                    </PieChart>
                  </ResponsiveContainer>
                </Box>
              ) : (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 2, textAlign: 'center' }}>
                  No risk data available
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Risk Trends */}
        <Grid item xs={12} md={8}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Risk Trends Over Time
              </Typography>
              {chartData.trendData.length > 0 ? (
                <Box sx={{ height: 300, mt: 2 }}>
                  <ResponsiveContainer>
                    <AreaChart data={chartData.trendData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="time" />
                      <YAxis />
                      <RechartsTooltip />
                      <Legend />
                      <Area type="monotone" dataKey="critical" stackId="1" stroke="#d32f2f" fill="#d32f2f" />
                      <Area type="monotone" dataKey="high" stackId="1" stroke="#ed6c02" fill="#ed6c02" />
                      <Area type="monotone" dataKey="medium" stackId="1" stroke="#0288d1" fill="#0288d1" />
                    </AreaChart>
                  </ResponsiveContainer>
                </Box>
              ) : (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 2, textAlign: 'center' }}>
                  No trend data available
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Top Risky MSISDNs */}
        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Top Risky MSISDNs
              </Typography>
              {chartData.topMsisdns.length > 0 ? (
                <Box sx={{ height: 300, mt: 2 }}>
                  <ResponsiveContainer>
                    <BarChart data={chartData.topMsisdns}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="msisdn" angle={-45} textAnchor="end" height={100} />
                      <YAxis />
                      <RechartsTooltip />
                      <Bar dataKey="count" fill="#8884d8" />
                    </BarChart>
                  </ResponsiveContainer>
                </Box>
              ) : (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 2, textAlign: 'center' }}>
                  No MSISDN data available
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Risk by Transaction Type */}
          <Grid item xs={12} md={3}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                By Transaction Type
              </Typography>
              {chartData.typeData.some(t => t.value > 0) ? (
                <Box sx={{ height: 300, mt: 2 }}>
                  <ResponsiveContainer>
                    <PieChart>
                      <Pie
                        data={chartData.typeData}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={({ name, value, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                        outerRadius={80}
                        fill="#8884d8"
                        dataKey="value"
                      >
                        {chartData.typeData.map((entry, index) => {
                          const colors = ['#0088FE', '#00C49F']
                          return <Cell key={`cell-${index}`} fill={colors[index % colors.length]} />
                        })}
                      </Pie>
                      <RechartsTooltip />
                    </PieChart>
                  </ResponsiveContainer>
                </Box>
              ) : (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 2, textAlign: 'center' }}>
                  No type data available
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Risk Score Distribution */}
        <Grid item xs={12} md={3}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Risk Score Distribution
              </Typography>
              {chartData.scoreDistribution.some(s => s.count > 0) ? (
                <Box sx={{ height: 300, mt: 2 }}>
                  <ResponsiveContainer>
                    <BarChart data={chartData.scoreDistribution} layout="vertical">
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis type="number" />
                      <YAxis dataKey="range" type="category" width={60} />
                      <RechartsTooltip />
                      <Bar dataKey="count" fill="#82ca9d" />
                    </BarChart>
                  </ResponsiveContainer>
                </Box>
              ) : (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 2, textAlign: 'center' }}>
                  No score data available
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Filters and Alerts */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 2 }}>
            <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
              <FormControl size="small" sx={{ minWidth: 150 }}>
                <InputLabel>Risk Level Filter</InputLabel>
                <Select value={filter} label="Risk Level Filter" onChange={(e) => {
                  setFilter(e.target.value as any)
                  setPage(1) // Reset to first page when filter changes
                }}>
                <MenuItem value="all">All Risks</MenuItem>
                <MenuItem value="high">High & Critical</MenuItem>
                <MenuItem value="critical">Critical Only</MenuItem>
              </Select>
            </FormControl>
              <Typography variant="body2" color="text.secondary" sx={{ ml: 'auto' }}>
                Showing {paginatedRisks.length} of {filteredRisks.length} risks
              </Typography>
            </Stack>
          </Paper>
          </Grid>
        <Grid item xs={12} md={4}>
          <Card sx={{ bgcolor: stats.critical > 0 ? 'error.light' : stats.high > 0 ? 'warning.light' : 'success.light' }}>
            <CardContent>
              <Stack direction="row" spacing={2} alignItems="center">
                <NotificationsActiveIcon sx={{ fontSize: 40 }} />
                <Box>
                  <Typography variant="h6">
                    {stats.critical > 0 ? `${stats.critical} Critical Alert${stats.critical > 1 ? 's' : ''}` : 
                     stats.high > 0 ? `${stats.high} High Risk Alert${stats.high > 1 ? 's' : ''}` : 
                     'No Active Alerts'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {stats.critical > 0 ? 'Immediate action required' : 
                     stats.high > 0 ? 'Review recommended' : 
                     'System operating normally'}
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
          </Grid>
        </Grid>

      {loading && <LinearProgress sx={{ mb: 2 }} />}

      {filteredRisks.length === 0 && !loading && (
        <Alert severity="success">No fraud risks detected. System is clean!</Alert>
      )}

      {filteredRisks.length > 0 && (
        <>
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Risk Level</TableCell>
                <TableCell>Risk Score</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>MSISDN</TableCell>
                <TableCell>Amount</TableCell>
                <TableCell>Flags</TableCell>
                <TableCell>Reason</TableCell>
                <TableCell>Timestamp</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedRisks.map((risk) => (
                <TableRow
                  key={risk.id}
                  hover
                  sx={{
                    cursor: 'pointer',
                    bgcolor: risk.riskLevel === 'critical' ? 'error.light' : risk.riskLevel === 'high' ? 'warning.light' : 'transparent',
                  }}
                  onClick={() => handleRowClick(risk)}
                >
                  <TableCell>
                    <Chip
                      label={risk.riskLevel.toUpperCase()}
                      size="small"
                      color={getRiskColor(risk.riskLevel)}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight="medium">
                      {risk.riskScore}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={risk.type === 'deposit' ? 'Deposit' : 'Withdrawal'}
                      size="small"
                      color={risk.type === 'deposit' ? 'success' : 'warning'}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                      {risk.msisdn}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight="medium">
                      {formatCurrency(risk.amount)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={0.5} flexWrap="wrap">
                      {risk.flags.map((flag, idx) => (
                        <Chip key={idx} label={flag} size="small" variant="outlined" />
                      ))}
                    </Stack>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {risk.reason}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {new Date(risk.timestamp).toLocaleString()}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="View Details">
                      <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleRowClick(risk) }}>
                        <CheckCircleIcon />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        
        {/* Pagination */}
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3, mb: 3 }}>
          <Pagination
            count={totalPages}
            page={page}
            onChange={(_, value) => setPage(value)}
            color="primary"
            size="large"
            showFirstButton
            showLastButton
          />
        </Box>
        </>
      )}

      <TransactionDetailModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        transaction={selectedTransaction}
        type={modalType}
      />

      {/* Test Rule Dialog */}
      <Dialog open={testDialogOpen} onClose={() => setTestDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Stack direction="row" spacing={1} alignItems="center">
            <SendIcon color="primary" />
            <Typography variant="h6">Test Rule: {testingRule?.name}</Typography>
          </Stack>
        </DialogTitle>
        <DialogContent>
          {testing && (
            <Box sx={{ py: 4, textAlign: 'center' }}>
              <LinearProgress sx={{ mb: 2 }} />
              <Typography>Testing rule and sending email...</Typography>
            </Box>
          )}
          
          {!testing && testResult && (
            <Stack spacing={3} sx={{ mt: 1 }}>
              {testResult.success ? (
                <>
                  <Alert severity={testResult.ruleMatched ? 'success' : 'warning'}>
                    {testResult.message}
                  </Alert>
                  
                  {testResult.ruleMatched && testResult.ruleEvaluation && (
                    <Paper sx={{ p: 2, bgcolor: 'background.default' }}>
                      <Typography variant="subtitle2" gutterBottom fontWeight="bold">
                        Rule Evaluation Result
                      </Typography>
                      <Stack spacing={1} sx={{ mt: 1 }}>
                        <Box>
                          <Typography variant="caption" color="text.secondary">Flag:</Typography>
                          <Typography variant="body2">{testResult.ruleEvaluation.flag}</Typography>
                        </Box>
                        <Box>
                          <Typography variant="caption" color="text.secondary">Risk Score:</Typography>
                          <Typography variant="body2" fontWeight="bold">{testResult.ruleEvaluation.riskScore} points</Typography>
                        </Box>
                        <Box>
                          <Typography variant="caption" color="text.secondary">Risk Level:</Typography>
                          <Chip 
                            label={testResult.ruleEvaluation.riskLevel.toUpperCase()} 
                            size="small"
                            color={
                              testResult.ruleEvaluation.riskLevel === 'critical' ? 'error' :
                              testResult.ruleEvaluation.riskLevel === 'high' ? 'warning' :
                              testResult.ruleEvaluation.riskLevel === 'medium' ? 'info' : 'default'
                            }
                            sx={{ ml: 1 }}
                          />
                        </Box>
                        <Box>
                          <Typography variant="caption" color="text.secondary">Action:</Typography>
                          <Chip 
                            label={testResult.ruleEvaluation.action} 
                            size="small"
                            sx={{ ml: 1 }}
                          />
                        </Box>
                      </Stack>
                    </Paper>
                  )}
                  
                  {testResult.testTransaction && (
                    <Paper sx={{ p: 2, bgcolor: 'background.default' }}>
                      <Typography variant="subtitle2" gutterBottom fontWeight="bold">
                        Test Transaction
                      </Typography>
                      <Stack spacing={1} sx={{ mt: 1 }}>
                        <Box>
                          <Typography variant="caption" color="text.secondary">Type:</Typography>
                          <Typography variant="body2">{testResult.testTransaction.type}</Typography>
                        </Box>
                        <Box>
                          <Typography variant="caption" color="text.secondary">MSISDN:</Typography>
                          <Typography variant="body2">{testResult.testTransaction.msisdn}</Typography>
                        </Box>
                        <Box>
                          <Typography variant="caption" color="text.secondary">Amount:</Typography>
                          <Typography variant="body2">KES {testResult.testTransaction.amount.toLocaleString()}</Typography>
                        </Box>
                        <Box>
                          <Typography variant="caption" color="text.secondary">Status:</Typography>
                          <Typography variant="body2">{testResult.testTransaction.status}</Typography>
                        </Box>
                      </Stack>
                    </Paper>
                  )}
                  
                  {testResult.email && (
                    <Paper sx={{ p: 2, bgcolor: 'background.default' }}>
                      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
                        <EmailIcon color="primary" />
                        <Typography variant="subtitle2" fontWeight="bold">
                          Email Notification
                        </Typography>
                      </Stack>
                      
                      {testResult.email.sent ? (
                        <Alert severity="success" sx={{ mb: 2 }}>
                          ✅ Test email sent successfully to: {testResult.email.preview?.to.join(', ')}
                        </Alert>
                      ) : (
                        <Alert severity="error" sx={{ mb: 2 }}>
                          ❌ Failed to send email: {testResult.email.error || 'Unknown error'}
                        </Alert>
                      )}
                      
                      {testResult.email.preview && (
                        <Box>
                          <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                            Email Preview:
                          </Typography>
                          <Paper variant="outlined" sx={{ p: 2, bgcolor: 'grey.50', mt: 1 }}>
                            <Box sx={{ mb: 2 }}>
                              <Typography variant="caption" color="text.secondary" display="block">To:</Typography>
                              <Typography variant="body2">{testResult.email.preview.to.join(', ')}</Typography>
                            </Box>
                            <Box sx={{ mb: 2 }}>
                              <Typography variant="caption" color="text.secondary" display="block">Subject:</Typography>
                              <Typography variant="body2" fontWeight="medium">{testResult.email.preview.subject}</Typography>
                            </Box>
                            <Box>
                              <Typography variant="caption" color="text.secondary" display="block" gutterBottom>Body:</Typography>
                              <Typography 
                                variant="body2" 
                                component="pre" 
                                sx={{ 
                                  whiteSpace: 'pre-wrap', 
                                  fontFamily: 'monospace',
                                  fontSize: '0.875rem',
                                  bgcolor: 'white',
                                  p: 1,
                                  borderRadius: 1,
                                  border: '1px solid',
                                  borderColor: 'divider',
                                }}
                              >
                                {testResult.email.preview.body}
                              </Typography>
                            </Box>
                          </Paper>
                        </Box>
                      )}
                    </Paper>
                  )}
                </>
              ) : (
                <Alert severity="error">{testResult.message}</Alert>
              )}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTestDialogOpen(false)}>Close</Button>
          {testResult && testResult.ruleMatched && (
            <Button 
              variant="contained" 
              startIcon={<SendIcon />}
              onClick={() => handleTestRule(testingRule!)}
            >
              Test Again
            </Button>
          )}
        </DialogActions>
      </Dialog>
        </>
      )}

      {tabValue === 1 && (
        <>
          <Paper sx={{ p: 2, mb: 3 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Typography variant="h6">Fraud Detection Rules</Typography>
              <Button variant="contained" startIcon={<AddIcon />} onClick={handleCreateRule}>
                Create Rule
              </Button>
            </Stack>
          </Paper>

          {loadingRules && <LinearProgress sx={{ mb: 2 }} />}

          {rules.length === 0 && !loadingRules && (
            <Alert severity="info">No rules configured. Create your first rule to start detecting fraud patterns.</Alert>
          )}

          {rules.length > 0 && (
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>Threshold</TableCell>
                    <TableCell>Risk Score</TableCell>
                    <TableCell>Action</TableCell>
                    <TableCell>Priority</TableCell>
                    <TableCell>Schedule</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rules.map((rule) => (
                    <TableRow key={rule.id}>
                      <TableCell>
                        <Typography variant="body2" fontWeight="medium">
                          {rule.name}
                        </Typography>
                        {rule.description && (
                          <Tooltip title={rule.description} arrow>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5, maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                              {rule.description.substring(0, 100)}{rule.description.length > 100 ? '...' : ''}
                            </Typography>
                          </Tooltip>
                        )}
                      </TableCell>
                      <TableCell>
                        <Chip label={rule.ruleType} size="small" variant="outlined" />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 'medium' }}>
                          {formatThreshold(rule)}
                        </Typography>
                      </TableCell>
                      <TableCell>{rule.riskScore}</TableCell>
                      <TableCell>
                        <Chip label={rule.action} size="small" color={rule.action === 'block' ? 'error' : rule.action === 'alert' ? 'warning' : 'default'} />
                      </TableCell>
                      <TableCell>{rule.priority}</TableCell>
                      <TableCell>
                        {rule.scheduleInterval && rule.scheduleInterval > 0 ? (
                          <Tooltip title={`Runs automatically every ${rule.scheduleInterval} hour(s)`}>
                            <Chip 
                              label={`Every ${rule.scheduleInterval}h`} 
                              size="small" 
                              color="info" 
                              variant="outlined"
                            />
                          </Tooltip>
                        ) : (
                          <Typography variant="caption" color="text.secondary">
                            On-demand
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <FormControlLabel
                          control={<Switch checked={rule.enabled} onChange={() => handleToggleRule(rule)} size="small" />}
                          label={rule.enabled ? 'Enabled' : 'Disabled'}
                        />
                      </TableCell>
                      <TableCell align="right">
                        <Stack direction="row" spacing={1} justifyContent="flex-end">
                          <Tooltip title="Test Rule">
                            <IconButton 
                              size="small" 
                              color="primary"
                              onClick={() => handleTestRule(rule)}
                              disabled={!rule.enabled}
                            >
                              <SendIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Edit">
                            <IconButton size="small" onClick={() => handleEditRule(rule)}>
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete">
                            <IconButton size="small" color="error" onClick={() => handleDeleteRule(rule.id)}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {/* Rule Dialog */}
          <Dialog open={ruleDialogOpen} onClose={() => setRuleDialogOpen(false)} maxWidth="md" fullWidth>
            <DialogTitle>{editingRule ? 'Edit Rule' : 'Create Rule'}</DialogTitle>
            <DialogContent>
              <Stack spacing={2} sx={{ mt: 1 }}>
                <TextField
                  label="Rule Name"
                  fullWidth
                  value={ruleForm.name}
                  onChange={(e) => setRuleForm({ ...ruleForm, name: e.target.value })}
                  required
                />
                <TextField
                  label="Description"
                  fullWidth
                  multiline
                  rows={4}
                  value={ruleForm.description}
                  onChange={(e) => setRuleForm({ ...ruleForm, description: e.target.value })}
                  helperText="Detailed explanation of why this rule is important and what it detects"
                />
                <FormControl fullWidth>
                  <InputLabel>Rule Type</InputLabel>
                  <Select
                    value={ruleForm.ruleType}
                    label="Rule Type"
                    onChange={(e) => {
                      const newType = e.target.value as any
                      const currentParams = ruleForm.conditionParams || {}
                      
                      // Preserve compatible values when switching types, otherwise use defaults
                      let newParams: any = {}
                      if (newType === 'amount') {
                        // Preserve operator and value if they exist
                        newParams = {
                          operator: currentParams.operator || '>',
                          value: currentParams.value !== undefined ? currentParams.value : 0
                        }
                      } else if (newType === 'frequency') {
                        newParams = {
                          operator: currentParams.operator || '>',
                          value: currentParams.value !== undefined ? currentParams.value : 0,
                          time_window: currentParams.time_window || '1h'
                        }
                      } else if (newType === 'volume') {
                        newParams = {
                          operator: currentParams.operator || '>',
                          value: currentParams.value !== undefined ? currentParams.value : 0,
                          time_window: currentParams.time_window || '24h'
                        }
                      } else if (newType === 'time') {
                        newParams = {
                          start_hour: currentParams.start_hour !== undefined ? currentParams.start_hour : 0,
                          end_hour: currentParams.end_hour !== undefined ? currentParams.end_hour : 23
                        }
                      } else if (newType === 'status') {
                        newParams = {
                          status: currentParams.status || 'FAILED'
                        }
                      } else if (newType === 'custom') {
                        // Keep existing params for custom type
                        newParams = currentParams
                      }
                      setRuleForm({ ...ruleForm, ruleType: newType, conditionParams: newParams })
                    }}
                  >
                    <MenuItem value="amount">Amount</MenuItem>
                    <MenuItem value="frequency">Frequency</MenuItem>
                    <MenuItem value="volume">Volume</MenuItem>
                    <MenuItem value="time">Time</MenuItem>
                    <MenuItem value="status">Status</MenuItem>
                    <MenuItem value="custom">Custom</MenuItem>
                  </Select>
                </FormControl>
                <TextField
                  label="Risk Score"
                  type="number"
                  fullWidth
                  value={ruleForm.riskScore}
                  onChange={(e) => setRuleForm({ ...ruleForm, riskScore: parseInt(e.target.value) || 0 })}
                  required
                  helperText="Points added to risk score when rule matches"
                />
                <FormControl fullWidth>
                  <InputLabel>Action</InputLabel>
                  <Select
                    value={ruleForm.action}
                    label="Action"
                    onChange={(e) => setRuleForm({ ...ruleForm, action: e.target.value as any })}
                  >
                    <MenuItem value="flag">Flag - Mark transaction for review (no immediate action)</MenuItem>
                    <MenuItem value="alert">Alert - Send email notification to fraud team</MenuItem>
                    <MenuItem value="block">Block - Automatically block/flag transaction (use with caution)</MenuItem>
                  </Select>
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, ml: 1.5 }}>
                    Action taken when this rule matches a transaction
                  </Typography>
                </FormControl>
                <TextField
                  label="Priority"
                  type="number"
                  fullWidth
                  value={ruleForm.priority}
                  onChange={(e) => setRuleForm({ ...ruleForm, priority: parseInt(e.target.value) || 0 })}
                  helperText="Higher priority rules are evaluated first"
                />
                <TextField
                  label="Schedule Interval (hours)"
                  type="number"
                  fullWidth
                  value={ruleForm.scheduleInterval || 0}
                  onChange={(e) => setRuleForm({ ...ruleForm, scheduleInterval: parseInt(e.target.value) || 0 })}
                  helperText="How often to run this rule automatically (0 = on-demand only). Examples: 6 = every 6 hours, 24 = daily, 168 = weekly"
                  inputProps={{ step: '1', min: '0' }}
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={ruleForm.enabled ?? true}
                      onChange={(e) => setRuleForm({ ...ruleForm, enabled: e.target.checked })}
                    />
                  }
                  label="Enabled"
                />
                
                {/* Condition Parameters based on Rule Type */}
                <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 2, bgcolor: 'background.default' }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Condition Parameters
                  </Typography>
                  
                  {ruleForm.ruleType === 'amount' && (
                    <Stack spacing={2}>
                      <FormControl fullWidth>
                        <InputLabel>Operator</InputLabel>
                        <Select
                          value={(ruleForm.conditionParams as any)?.operator || '>'}
                          label="Operator"
                          onChange={(e) => setRuleForm({
                            ...ruleForm,
                            conditionParams: { ...(ruleForm.conditionParams as any || {}), operator: e.target.value }
                          })}
                        >
                          <MenuItem value=">">Greater than (&gt;)</MenuItem>
                          <MenuItem value=">=">Greater than or equal (&gt;=)</MenuItem>
                          <MenuItem value="<">Less than (&lt;)</MenuItem>
                          <MenuItem value="<=">Less than or equal (&lt;=)</MenuItem>
                          <MenuItem value="==">Equal to (==)</MenuItem>
                        </Select>
                      </FormControl>
                      <TextField
                        label="Amount (KES)"
                        type="number"
                        fullWidth
                        value={(ruleForm.conditionParams as any)?.value || ''}
                        onChange={(e) => setRuleForm({
                          ...ruleForm,
                          conditionParams: { ...(ruleForm.conditionParams as any || {}), value: parseFloat(e.target.value) || 0 }
                        })}
                        helperText="Transaction amount threshold"
                        inputProps={{ step: '0.01', min: '0' }}
                      />
                    </Stack>
                  )}
                  
                  {ruleForm.ruleType === 'frequency' && (
                    <Stack spacing={2}>
                      <FormControl fullWidth>
                        <InputLabel>Operator</InputLabel>
                        <Select
                          value={(ruleForm.conditionParams as any)?.operator || '>'}
                          label="Operator"
                          onChange={(e) => setRuleForm({
                            ...ruleForm,
                            conditionParams: { ...(ruleForm.conditionParams as any || {}), operator: e.target.value }
                          })}
                        >
                          <MenuItem value=">">Greater than (&gt;)</MenuItem>
                          <MenuItem value=">=">Greater than or equal (&gt;=)</MenuItem>
                        </Select>
                      </FormControl>
                      <TextField
                        label="Number of Transactions"
                        type="number"
                        fullWidth
                        value={(ruleForm.conditionParams as any)?.value || ''}
                        onChange={(e) => setRuleForm({
                          ...ruleForm,
                          conditionParams: { ...(ruleForm.conditionParams as any || {}), value: parseInt(e.target.value) || 0 }
                        })}
                        helperText="Transaction count threshold"
                        inputProps={{ step: '1', min: '0' }}
                      />
                      <TextField
                        label="Time Window"
                        fullWidth
                        value={(ruleForm.conditionParams as any)?.time_window || '1h'}
                        onChange={(e) => setRuleForm({
                          ...ruleForm,
                          conditionParams: { ...(ruleForm.conditionParams as any || {}), time_window: e.target.value }
                        })}
                        helperText="Format: 15m, 1h, 24h, 168h (7 days)"
                        placeholder="1h"
                      />
                    </Stack>
                  )}
                  
                  {ruleForm.ruleType === 'volume' && (
                    <Stack spacing={2}>
                      <FormControl fullWidth>
                        <InputLabel>Operator</InputLabel>
                        <Select
                          value={(ruleForm.conditionParams as any)?.operator || '>'}
                          label="Operator"
                          onChange={(e) => setRuleForm({
                            ...ruleForm,
                            conditionParams: { ...(ruleForm.conditionParams as any || {}), operator: e.target.value }
                          })}
                        >
                          <MenuItem value=">">Greater than (&gt;)</MenuItem>
                          <MenuItem value=">=">Greater than or equal (&gt;=)</MenuItem>
                        </Select>
                      </FormControl>
                      <TextField
                        label="Total Volume (KES)"
                        type="number"
                        fullWidth
                        value={(ruleForm.conditionParams as any)?.value || ''}
                        onChange={(e) => setRuleForm({
                          ...ruleForm,
                          conditionParams: { ...(ruleForm.conditionParams as any || {}), value: parseFloat(e.target.value) || 0 }
                        })}
                        helperText="Total transaction volume threshold"
                        inputProps={{ step: '0.01', min: '0' }}
                      />
                      <TextField
                        label="Time Window"
                        fullWidth
                        value={(ruleForm.conditionParams as any)?.time_window || '24h'}
                        onChange={(e) => setRuleForm({
                          ...ruleForm,
                          conditionParams: { ...(ruleForm.conditionParams as any || {}), time_window: e.target.value }
                        })}
                        helperText="Format: 1h, 24h, 168h (7 days)"
                        placeholder="24h"
                      />
                    </Stack>
                  )}
                  
                  {ruleForm.ruleType === 'time' && (
                    <Stack spacing={2}>
                      <TextField
                        label="Start Hour (0-23)"
                        type="number"
                        fullWidth
                        value={(ruleForm.conditionParams as any)?.start_hour ?? 0}
                        onChange={(e) => setRuleForm({
                          ...ruleForm,
                          conditionParams: { ...(ruleForm.conditionParams as any || {}), start_hour: parseInt(e.target.value) || 0 }
                        })}
                        helperText="Hour when suspicious time window starts"
                        inputProps={{ step: '1', min: '0', max: '23' }}
                      />
                      <TextField
                        label="End Hour (0-23)"
                        type="number"
                        fullWidth
                        value={(ruleForm.conditionParams as any)?.end_hour ?? 23}
                        onChange={(e) => setRuleForm({
                          ...ruleForm,
                          conditionParams: { ...(ruleForm.conditionParams as any || {}), end_hour: parseInt(e.target.value) || 23 }
                        })}
                        helperText="Hour when suspicious time window ends (can cross midnight)"
                        inputProps={{ step: '1', min: '0', max: '23' }}
                      />
                    </Stack>
                  )}
                  
                  {ruleForm.ruleType === 'status' && (
                    <TextField
                      label="Status"
                      fullWidth
                      value={(ruleForm.conditionParams as any)?.status || 'FAILED'}
                      onChange={(e) => setRuleForm({
                        ...ruleForm,
                        conditionParams: { ...(ruleForm.conditionParams as any || {}), status: e.target.value }
                      })}
                      helperText="Transaction status to flag (e.g., FAILED, PENDING)"
                    />
                  )}
                  
                  {ruleForm.ruleType === 'custom' && (
                    <TextField
                      label="JSON Parameters"
                      fullWidth
                      multiline
                      rows={4}
                      value={JSON.stringify(ruleForm.conditionParams || {}, null, 2)}
                      onChange={(e) => {
                        try {
                          const parsed = JSON.parse(e.target.value)
                          setRuleForm({ ...ruleForm, conditionParams: parsed })
                        } catch {
                          // Invalid JSON, keep as is
                        }
                      }}
                      helperText="Enter condition parameters as JSON"
                    />
                  )}
                </Box>
                
                {/* Rule Preview */}
                <Box sx={{ border: '1px solid', borderColor: 'primary.main', borderRadius: 1, p: 2, bgcolor: 'action.hover' }}>
                  <Typography variant="subtitle2" gutterBottom fontWeight="bold">
                    Rule Preview
                  </Typography>
                  <Stack spacing={0.5}>
                    <Typography variant="body2">
                      <strong>Name:</strong> {ruleForm.name || '(not set)'}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Threshold:</strong> {ruleForm.ruleType ? formatThreshold({ ...ruleForm, conditionParams: ruleForm.conditionParams || {} } as FraudRule) : '(not set)'}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Action:</strong> {ruleForm.action === 'flag' ? 'Flag for review' : ruleForm.action === 'alert' ? 'Send email alert' : ruleForm.action === 'block' ? 'Block transaction' : '(not set)'}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Risk Score:</strong> +{ruleForm.riskScore || 0} points
                    </Typography>
                    <Typography variant="body2">
                      <strong>Priority:</strong> {ruleForm.priority || 0} (higher = evaluated first)
                    </Typography>
                    <Typography variant="body2">
                      <strong>Schedule:</strong> {ruleForm.scheduleInterval && ruleForm.scheduleInterval > 0 
                        ? `Every ${ruleForm.scheduleInterval} hour(s)` 
                        : 'On-demand only'}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Status:</strong> {ruleForm.enabled ? '✅ Enabled' : '❌ Disabled'}
                    </Typography>
                  </Stack>
                </Box>
              </Stack>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setRuleDialogOpen(false)}>Cancel</Button>
              <Button variant="contained" onClick={handleSaveRule} disabled={!ruleForm.name || ruleForm.riskScore === undefined}>
                {editingRule ? 'Update Rule' : 'Create Rule'}
              </Button>
            </DialogActions>
          </Dialog>
        </>
      )}

      {tabValue === 2 && (
        <>
          <Paper sx={{ p: 2, mb: 3 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Typography variant="h6">Notification Emails</Typography>
              <Button variant="contained" startIcon={<AddIcon />} onClick={handleCreateEmail}>
                Add Email
              </Button>
            </Stack>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Configure email addresses to receive fraud detection alerts. You can customize which risk levels trigger notifications for each email.
            </Typography>
          </Paper>

          {loadingEmails ? (
            <LinearProgress />
          ) : (
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Email Address</TableCell>
                    <TableCell>Critical</TableCell>
                    <TableCell>High</TableCell>
                    <TableCell>Medium</TableCell>
                    <TableCell>Low</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {notificationEmails.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} align="center">
                        <Typography variant="body2" color="text.secondary" sx={{ py: 3 }}>
                          No notification emails configured. Click "Add Email" to add one.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    notificationEmails.map((email) => (
                      <TableRow key={email.id}>
                        <TableCell>
                          <Typography variant="body2" fontWeight="medium">
                            {email.email}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={email.notifyOnCritical ? 'Yes' : 'No'}
                            size="small"
                            color={email.notifyOnCritical ? 'error' : 'default'}
                            variant={email.notifyOnCritical ? 'filled' : 'outlined'}
                          />
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={email.notifyOnHigh ? 'Yes' : 'No'}
                            size="small"
                            color={email.notifyOnHigh ? 'warning' : 'default'}
                            variant={email.notifyOnHigh ? 'filled' : 'outlined'}
                          />
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={email.notifyOnMedium ? 'Yes' : 'No'}
                            size="small"
                            color={email.notifyOnMedium ? 'info' : 'default'}
                            variant={email.notifyOnMedium ? 'filled' : 'outlined'}
                          />
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={email.notifyOnLow ? 'Yes' : 'No'}
                            size="small"
                            color={email.notifyOnLow ? 'success' : 'default'}
                            variant={email.notifyOnLow ? 'filled' : 'outlined'}
                          />
                        </TableCell>
                        <TableCell>
                          <FormControlLabel
                            control={
                              <Switch
                                checked={email.enabled}
                                onChange={() => handleToggleEmail(email)}
                                size="small"
                              />
                            }
                            label={email.enabled ? 'Enabled' : 'Disabled'}
                          />
                        </TableCell>
                        <TableCell align="right">
                          <Stack direction="row" spacing={1} justifyContent="flex-end">
                            <Tooltip title="Edit">
                              <IconButton size="small" onClick={() => handleEditEmail(email)}>
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Delete">
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => handleDeleteEmail(email.id)}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {/* Email Dialog */}
          <Dialog open={emailDialogOpen} onClose={() => setEmailDialogOpen(false)} maxWidth="sm" fullWidth>
            <DialogTitle>{editingEmail ? 'Edit Notification Email' : 'Add Notification Email'}</DialogTitle>
            <DialogContent>
              <Stack spacing={2} sx={{ mt: 1 }}>
                <TextField
                  label="Email Address"
                  type="email"
                  fullWidth
                  value={emailForm.email}
                  onChange={(e) => setEmailForm({ ...emailForm, email: e.target.value })}
                  required
                  disabled={!!editingEmail}
                  helperText={editingEmail ? 'Email address cannot be changed' : 'Enter the email address to receive fraud alerts'}
                />
                <Typography variant="subtitle2" sx={{ mt: 2 }}>
                  Notification Preferences
                </Typography>
                <FormControlLabel
                  control={
                    <Switch
                      checked={emailForm.notifyOnCritical ?? true}
                      onChange={(e) => setEmailForm({ ...emailForm, notifyOnCritical: e.target.checked })}
                    />
                  }
                  label="Notify on Critical Risk (80-100 score)"
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={emailForm.notifyOnHigh ?? true}
                      onChange={(e) => setEmailForm({ ...emailForm, notifyOnHigh: e.target.checked })}
                    />
                  }
                  label="Notify on High Risk (60-79 score)"
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={emailForm.notifyOnMedium ?? false}
                      onChange={(e) => setEmailForm({ ...emailForm, notifyOnMedium: e.target.checked })}
                    />
                  }
                  label="Notify on Medium Risk (50-59 score)"
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={emailForm.notifyOnLow ?? false}
                      onChange={(e) => setEmailForm({ ...emailForm, notifyOnLow: e.target.checked })}
                    />
                  }
                  label="Notify on Low Risk (0-49 score)"
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={emailForm.enabled ?? true}
                      onChange={(e) => setEmailForm({ ...emailForm, enabled: e.target.checked })}
                    />
                  }
                  label="Enabled"
                />
              </Stack>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setEmailDialogOpen(false)}>Cancel</Button>
              <Button variant="contained" onClick={handleSaveEmail} disabled={!emailForm.email}>
                {editingEmail ? 'Update' : 'Add'}
              </Button>
            </DialogActions>
          </Dialog>
        </>
      )}
    </Box>
  )
}

export default FraudDetection

